import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllCantars,
  deleteCantar,
  archiveCantar,
  restoreCantar,
  bulkArchiveCantar,
  bulkRestoreCantar,
  bulkDeleteCantar,
} from '../../redux/actions/cantar';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import Loader from '../../../../components/Loader';
import EnhancedTable from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import DeleteDialog from 'components/DeleteDialog';
import { IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import FilterBar from '../../components/FilterBar';
import SettingDialog from 'containers/SuperAdmin/components/SettingDialog';
import Archive from '@material-ui/icons/Archive';
const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

export default function Cantar({ name, id }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [view, setView] = useState('normal');
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [sort, setSort] = useState({ orderBy: '_id', orderDir: 'desc' });

  const { cantars, fetching } = useSelector(({ admin }) => admin.cantar);

  const loadPage = (page, sortOptions = {}) => {
    if (Object.values(sortOptions).length === 0) {
      sortOptions = sort;
    }
    let update = { page, limit: cantars.limit, ...sortOptions };
    if (selectedTopics) {
      update = { ...update, topics: selectedTopics.toString() };
    }
    if (searchString !== '') {
      update = { ...update, search: searchString };
    }
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllCantars(update));
    setSort(sortOptions);
  };

  const headCells = [
    {
      id: 'checkbox',
      label: 'Select',
    },
    { id: 'name', label: 'Name' },
    { id: 'order', label: 'Order' },
    {
      id: 'actions',
      label: 'Actions',
      actions:
        view === 'normal'
          ? [
              {
                label: 'Edit',
                action: (r) => {
                  history.push(`/superadmin/cantars/edit/${r._id}`);
                },
                icon: <EditIcon />,
                disabled: () => false,
              },
              {
                label: 'Archive',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <Archive />,
                disabled: () => false,
              },
            ]
          : [
              {
                label: 'Restore',
                action: (r) => {
                  dispatch(restoreCantar(r._id));
                },
                icon: <RestoreFromTrashIcon />,
                disabled: () => false,
              },
              {
                label: 'Permanently Delete',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <DeleteForeverIcon />,
                disabled: () => false,
              },
            ],
    },
  ];

  const handleSearch = (d) => {
    setSearchString(d);
    let update = {
      page: cantars.page,
      limit: cantars.limit,
      search: d,
      ...sort,
    };
    if (selectedTopics) {
      update = { ...update, topics: selectedTopics.toString() };
    }
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllCantars(update));
  };

  const handleAction = () => {
    let update = {
      page: cantars.page,
      limit: cantars.limit,
      topics: selectedTopics.toString(),
      ...sort,
    };
    if (searchString !== '') {
      update = { ...update, search: searchString };
    }
    if (view === 'normal') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllCantars(update));
    setView(view === 'normal' ? 'archived' : 'normal');
  };

  const handleTopic = (e) => {
    setSelectedTopics(e);
    let update = {
      page: cantars.page,
      limit: cantars.limit,
      topics: e.toString(),
      ...sort,
    };
    if (searchString !== '') {
      update = { ...update, search: searchString };
    }
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllCantars(update));
  };

  useEffect(() => {
    dispatch(fetchAllCantars());
  }, [dispatch]);

  return (
    <div className={classes.root}>
      {fetching && <Loader fetching={fetching} />}
      <PageHeader
        disableSearch
        title={name}
        options={
          <>
            <SettingDialog id={id} name={name} enableLabel={false} />
            <IconButton
              onClick={() => {
                history.push('/superadmin/cantars/new');
              }}
            >
              <AddIcon />
            </IconButton>
          </>
        }
      />
      <FilterBar
        enableGroups={false}
        topics={selectedTopics}
        toggle={view === 'archived'}
        onSearch={handleSearch}
        onTopicSelect={handleTopic}
        onAction={handleAction}
      />
      {cantars && cantars.docs.length > 0 && (
        <Grid container direction='row' justifyContent='flex-start' spacing={2}>
          <Grid item sm={12} xs={12}>
            <EnhancedTable
              data={{
                ...cantars,
                docs: cantars.docs.filter((x) =>
                  view === 'normal' ? !x.isArchived : x.isArchived
                ),
              }}
              view={view}
              headCells={headCells}
              orderField={sort.orderBy}
              orderDirection={sort.orderDir}
              handlePage={loadPage}
              handleOrder={(d) => loadPage(cantars.page, d)}
              handleBulk={(ids) => {
                if (view === 'normal') {
                  dispatch(bulkArchiveCantar(ids));
                } else {
                  dispatch(bulkRestoreCantar(ids));
                }
              }}
              handleBulkDelete={(ids) => dispatch(bulkDeleteCantar(ids))}
            />
          </Grid>
        </Grid>
      )}
      <DeleteDialog
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        handleConfirm={() => {
          if (view === 'archived') {
            dispatch(deleteCantar(deleteId));
          } else {
            dispatch(archiveCantar(deleteId));
          }
          setDeleteId(null);
          setOpenDeleteDialog(false);
        }}
      />
    </div>
  );
}
